import React, { useCallback, useMemo, useState } from 'react';
import amplitudeLog from '@client/amplitude';
import { AMP_APP_OPEN_EPD_SELECTOR } from '@shared/constants';
import { required } from '@shared/helpers/function.helpers';
import {
  getGenericProductId,
  hasProductElementEpd,
} from '@shared/helpers/product-element.helpers';
import { isProductElement } from '@shared/helpers/recursive-element.helpers';
import { ProductID } from '@shared/models/product.model';
import { ProductSettingsProps } from '@features/products/ProductSettings';
import {
  addIdToLocallyStoredEPDIds,
  useAppliedEpdsFromGenericProduct,
  useClearEpdOnProductElements,
  useSetEpdOnProductElements,
} from '@features/products/use-product-switch.hooks';
import { useSelectedVersion } from '@client/store/ui';
import { Menu } from '@ui/menus/Menu';
import { DropdownIndicator } from '@ui/DropdownIndicator';
import { TextField, Button, TextFieldProps } from '@mui/material';
import { EllipsisText } from '@ui/EllipsisText/EllipsisText';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { tryCatch } from '@shared/helpers/utils.helpers';
import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { EPDMenuContent, EpdSelectAction } from './EPDMenuContent';

interface EPDMenuProps extends Pick<
  ProductSettingsProps,
  | 'product_id'
  | 'generic_product_id'
  | 'productCategoryElement'
  | 'generatedElements'
> {
  variant?: 'button' | 'select';
  onSelect?: (id: ProductID, genericId?: ProductID) => void;
}

const EPDMenu: React.FC<EPDMenuProps> = ({
  variant = 'select',
  product_id,
  generic_product_id,
  productCategoryElement,
  generatedElements,
  onSelect,
}) => {
  const selectedVersion = required(useSelectedVersion());

  const hasEPD = hasProductElementEpd({ product_id, generic_product_id });
  const genericProductId = getGenericProductId({
    product_id,
    generic_product_id,
  });

  const productCategoryElementChildren = useMemo(
    () =>
      productCategoryElement?.elements
        .filter(isProductElement)
        .filter((child) => getGenericProductId(child) === genericProductId) ??
      [],
    [productCategoryElement, genericProductId],
  );
  const productElements = generatedElements ?? productCategoryElementChildren;

  const appliedEPD = useMemo(
    () => (hasEPD ? selectedVersion.products[product_id] : undefined),
    [hasEPD, product_id, selectedVersion.products],
  );

  const mappedEPDs = useAppliedEpdsFromGenericProduct(
    genericProductId,
    appliedEPD,
  );

  const clearEPD = useClearEpdOnProductElements();
  const setEpdOnProductElements = useSetEpdOnProductElements();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const readonly = useIsReadonly();

  const selectedEpdId = useMemo(() => {
    const mappedIds = mappedEPDs.map(({ id }) => id);
    const appliedId = appliedEPD?.id ?? '';
    return mappedIds.includes(appliedId) ? appliedId : '';
  }, [appliedEPD?.id, mappedEPDs]);

  const selectedLabel = useMemo(() => {
    const value = selectedEpdId;
    if (!value) return 'Select EPD';
    return (
      mappedEPDs.find((item) => item.id === value)?.name ??
      appliedEPD?.name ??
      'Select EPD'
    );
  }, [appliedEPD?.name, mappedEPDs, selectedEpdId]);

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (variant === 'select' && readonly) return;
      amplitudeLog(AMP_APP_OPEN_EPD_SELECTOR);
      setMenuAnchor(event.currentTarget);
    },
    [readonly, variant],
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleSelectAction = useCallback(
    async (action: EpdSelectAction) => {
      switch (action.type) {
        case 'none': {
          await clearEPD(productElements);
          onSelect?.(genericProductId);
          // In case another user has applied the EPD, it might not be stored in the user client (and therefore disappear from the menu when removed).
          addIdToLocallyStoredEPDIds(genericProductId, selectedEpdId);
          break;
        }
        case 'epd': {
          const result = tryCatch(() =>
            setEpdOnProductElements(productElements, action.id),
          );
          if (result.ok) onSelect?.(action.id, genericProductId);
          break;
        }
      }
      handleCloseMenu();
    },
    [
      clearEPD,
      genericProductId,
      handleCloseMenu,
      onSelect,
      productElements,
      selectedEpdId,
      setEpdOnProductElements,
    ],
  );

  const slotProps = useObjectMemo<NonNullable<TextFieldProps['slotProps']>>({
    input: { readOnly: true },
    htmlInput: { sx: { cursor: 'pointer' } },
  });
  return (
    <>
      {variant === 'select' && (
        <TextField
          fullWidth
          size="small"
          label="Select EPD"
          value={selectedLabel ?? ''}
          onClick={handleOpenMenu}
          slotProps={slotProps}
          disabled={readonly}
        />
      )}

      {variant === 'button' && (
        <Button
          color="inherit"
          variant="text"
          endIcon={<DropdownIndicator open={Boolean(menuAnchor)} />}
          disabled={readonly}
          sx={{ textTransform: 'none', minWidth: 0 }}
          onClick={handleOpenMenu}
        >
          <EllipsisText showTooltip={true}>
            {appliedEPD?.name ?? selectedLabel}
          </EllipsisText>
        </Button>
      )}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        searchable="Search EPDs"
        width="auto"
      >
        <EPDMenuContent
          epds={mappedEPDs}
          selectedEpdIds={selectedEpdId ? [selectedEpdId] : []}
          productCategoryElement={productCategoryElement}
          genericProductId={genericProductId}
          onEpdSelect={handleSelectAction}
        />
      </Menu>
    </>
  );
};

export default EPDMenu;
